import React, { useEffect, useMemo, useState } from 'react';

const escapeRegExp = (value = '') => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const getQueryTokens = (value = '') =>
  Array.from(new Set(String(value || '').toLowerCase().trim().split(/\s+/).filter(Boolean)));

const fuzzySubsequenceScore = (needle = '', haystack = '') => {
  const n = String(needle || '').toLowerCase();
  const h = String(haystack || '').toLowerCase();
  if (!n || !h) return 0;
  if (h.includes(n)) return 120 - Math.min(h.indexOf(n), 40);
  let pos = 0;
  let penalty = 0;
  for (let i = 0; i < n.length; i += 1) {
    const found = h.indexOf(n[i], pos);
    if (found < 0) return 0;
    penalty += Math.max(0, found - pos);
    pos = found + 1;
  }
  return Math.max(0, 70 - penalty);
};

const highlightText = (text = '', query = '') => {
  const tokens = getQueryTokens(query);
  if (!tokens.length) return text;
  const ordered = [...tokens].sort((a, b) => b.length - a.length);
  const tokenSet = new Set(tokens);
  const re = new RegExp(`(${ordered.map(escapeRegExp).join('|')})`, 'ig');
  return String(text).split(re).map((part, idx) => (
    tokenSet.has(part.toLowerCase())
      ? <mark key={idx} className="internal-link-picker-mark">{part}</mark>
      : <React.Fragment key={idx}>{part}</React.Fragment>
  ));
};

const toSnippet = (text = '', query = '') => {
  const raw = String(text || '').trim();
  if (!raw) return '';
  const lower = raw.toLowerCase();
  const tokens = getQueryTokens(query);
  const firstIdx = tokens
    .map((token) => lower.indexOf(token))
    .filter((idx) => idx >= 0)
    .reduce((min, idx) => Math.min(min, idx), Number.POSITIVE_INFINITY);
  if (!Number.isFinite(firstIdx)) return raw.slice(0, 90);
  const start = Math.max(0, firstIdx - 22);
  const end = Math.min(raw.length, firstIdx + 68);
  const prefix = start > 0 ? '…' : '';
  const suffix = end < raw.length ? '…' : '';
  return `${prefix}${raw.slice(start, end)}${suffix}`;
};

const scoreTarget = (target, query) => {
  const tokens = getQueryTokens(query);
  const searchFields = Array.isArray(target?.searchFields) ? target.searchFields : [{ label: 'Title', text: target?.title || '' }];
  if (!tokens.length) {
    return {
      score: 0,
      bestField: searchFields[0] || { label: 'Title', text: target?.title || '' },
      tokenCount: 0,
    };
  }

  let score = 0;
  let tokenCount = 0;
  let bestField = null;
  let bestFieldScore = 0;
  const loweredTokens = tokens.map((t) => t.toLowerCase());

  searchFields.forEach((field) => {
    const text = String(field?.text || '');
    const lower = text.toLowerCase();
    if (!lower) return;

    let fieldScore = 0;
    let matchedHere = 0;
    loweredTokens.forEach((token) => {
      if (lower.includes(token)) {
        fieldScore += 45;
        matchedHere += 1;
      } else {
        fieldScore += fuzzySubsequenceScore(token, lower) > 25 ? 8 : 0;
      }
    });
    fieldScore += fuzzySubsequenceScore(query, text);
    if (field.label === 'Title') fieldScore += 18;
    score += fieldScore;
    tokenCount += matchedHere;
    if (!bestField || fieldScore > bestFieldScore) {
      bestField = field;
      bestFieldScore = fieldScore;
    }
  });

  return { score, bestField: bestField || { label: 'Title', text: target?.title || '' }, tokenCount };
};

export default function InternalLinkPickerModal({
  open = false,
  targets = [],
  title = 'Link to note or folder',
  onClose,
  onSelect,
}) {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActiveIndex(0);
  }, [open]);

  const filtered = useMemo(() => {
    const base = Array.isArray(targets) ? targets : [];
    const needle = query.trim();
    if (!needle) {
      return base.slice(0, 30).map((item) => ({ ...item, _match: scoreTarget(item, '') }));
    }
    return base
      .map((item) => ({ ...item, _match: scoreTarget(item, needle) }))
      .filter((item) => item._match.score > 0)
      .sort((a, b) => {
        if (a._match.score !== b._match.score) return b._match.score - a._match.score;
        if (a._match.tokenCount !== b._match.tokenCount) return b._match.tokenCount - a._match.tokenCount;
        return String(a.title || '').localeCompare(String(b.title || ''), undefined, { sensitivity: 'base' });
      })
      .slice(0, 30);
  }, [query, targets]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      onClose?.();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (activeIndex > filtered.length - 1) {
      setActiveIndex(Math.max(filtered.length - 1, 0));
    }
  }, [activeIndex, filtered.length]);

  if (!open) return null;

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose?.();
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((prev) => Math.min(prev + 1, Math.max(filtered.length - 1, 0)));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((prev) => Math.max(prev - 1, 0));
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const selected = filtered[activeIndex] || filtered[0];
      if (selected) onSelect?.(selected);
    }
  };

  return (
    <div className="internal-link-picker-backdrop" onMouseDown={onClose}>
      <div
        className="internal-link-picker-modal"
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Insert note or folder link"
      >
        <div className="internal-link-picker-title">{title}</div>
        <input
          className="search-input internal-link-picker-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Search notes and folders"
          autoFocus
        />
        <div className="internal-link-picker-list" role="listbox">
          {filtered.length === 0 ? (
            <div className="internal-link-picker-empty">No matches</div>
          ) : (
            filtered.map((item, idx) => (
              <button
                type="button"
                key={`${item.targetType}:${item.id}`}
                className={`internal-link-picker-item ${idx === activeIndex ? 'internal-link-picker-item-active' : ''}`}
                onMouseEnter={() => setActiveIndex(idx)}
                onClick={() => onSelect?.(item)}
              >
                <span className="internal-link-picker-main">
                  <span className="internal-link-picker-label">{highlightText(item.title, query)}</span>
                  {query.trim() && item?._match?.bestField?.text && (
                    <span className="internal-link-picker-preview">
                      <span className="internal-link-picker-preview-label">{item._match.bestField.label}:</span>{' '}
                      {highlightText(toSnippet(item._match.bestField.text, query), query)}
                    </span>
                  )}
                </span>
                <span className={`internal-link-picker-kind internal-link-picker-kind-${item.targetType}`}>
                  {item.targetType === 'folder' ? 'Folder' : 'Note'}
                </span>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
